import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Carousel } from '@bingads-webui-react/primitive-unthemed';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getPreviewCarouselStyles } from './styles';

class PreviewCarouselRaw extends React.PureComponent {
  constructor(props) {
    super(props);

    this.carouselRef = React.createRef();
    this.state = {
      direction: null,
    };
  }

  componentDidMount() {
    if (this.props.enableAutoPlay) {
      this.setAutoRotate();
    }
  }

  componentDidUpdate() {
    if (this.props.enableAutoPlay && !this.autoRotateListener) {
      this.setAutoRotate();
    }
  }

  componentWillUnmount() {
    this.clearAutoRotateListener();
  }

  setAutoRotate = () => {
    // disable auto-rotate in test
    /* istanbul ignore if */
    if (window.e2eTestEnvironment) {
      return;
    }

    this.clearAutoRotateListener();
    const { index, setIndex } = this.props;

    const indicators = this.carouselRef.current && this.carouselRef.current.querySelector('.new-preview-carousel-container-adprelib .carousel-indicators');

    // make sure we have the correct preview to rotate
    const previewCount = (indicators && indicators.childElementCount);
    let nextIndex = index + 1;

    /* istanbul ignore next */
    if (nextIndex >= previewCount) {
      nextIndex = 0;
    }

    this.autoRotateListener = setTimeout(() => {
      /* istanbul ignore next */
      this.setState({
        direction: 'next',
      });
      setIndex(nextIndex);
      this.setAutoRotate();
    }, 3000);
  };

  clearAutoRotateListener = () => {
    if (this.autoRotateListener) {
      clearTimeout(this.autoRotateListener);
      this.autoRotateListener = null;
    }
  };

  handleSelect = (selectedIndex, e) => {
    this.setState({
      direction: e.direction,
    }, () => this.clearAutoRotateListener());

    this.props.setIndex(selectedIndex);
  };

  render() {
    const { direction } = this.state;
    const {
      previewElements,
      index,
      hideNavigationControlsIfSingle,
      moveNavigationControlsToBottom,
      classes,
    } = this.props;

    let carouselClasses;

    if (hideNavigationControlsIfSingle && previewElements.length === 1) {
      carouselClasses = classes.hideNavigation;
    } else {
      carouselClasses = moveNavigationControlsToBottom
        ? classnames(classes.bottomCarouselNavigation, 'new-carousel-navigation-mma-adprelib')
        : classes.carouselNavigation;
    }

    return (
      <div className={classes.carouselRoot} ref={this.carouselRef}>
        <Carousel
          activeIndex={index}
          direction={direction}
          onSelect={this.handleSelect}
          controls={false}
          nextLabel={this.props.i18n.getString(_TL_('Next ad preview'))}
          prevLabel={this.props.i18n.getString(_TL_('Previous ad preview'))}
          // styling for carousel controls is handled by a more specific static class name selector
          // in some styles injected into the top of grid form for MMA editor.
          // will need to GA this new ad preview library before we can remove those selectors and directly
          // use the styles defined in this package
          className={carouselClasses}
        >
          {
            previewElements.map(previewElement => (
              <Carousel.Item>
                {previewElement}
              </Carousel.Item>
            ))
          }
        </Carousel>
      </div>
    );
  }
}

PreviewCarouselRaw.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  previewElements: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  classes: PropTypes.shape({
    carouselRoot: PropTypes.string.isRequired,
    carouselNavigation: PropTypes.string.isRequired,
    bottomCarouselNavigation: PropTypes.string.isRequired,
    hideNavigation: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  setIndex: PropTypes.func.isRequired,
  hideNavigationControlsIfSingle: PropTypes.bool,
  moveNavigationControlsToBottom: PropTypes.bool,
  enableAutoPlay: PropTypes.bool,
};

PreviewCarouselRaw.defaultProps = {
  hideNavigationControlsIfSingle: false,
  moveNavigationControlsToBottom: true,
  enableAutoPlay: true,
};

export const PreviewCarousel = (props) => {
  const { styleOverrides, previewElements } = props;
  // eslint-disable-next-line arrow-body-style
  const StyledPreviewCarousel = useMemo(() => {
    return withDefaultStyles(PreviewCarouselRaw, getPreviewCarouselStyles({ styleOverrides: styleOverrides.previewCarousel, numberOfElements: previewElements.length }));
  }, [styleOverrides, previewElements]);

  return (
    <StyledPreviewCarousel
      {...props}
    />
  );
};

PreviewCarousel.propTypes = {
  styleOverrides: PropTypes.shape({
    previewCarousel: PropTypes.shape({}),
  }),
  previewElements: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

PreviewCarousel.defaultProps = {
  styleOverrides: {},
};
